import { type Manifest } from '@doxyme/extensions-graphql-types';
import { Specs } from '@doxyme/extensions-common';

const BackendCapabilityID = Specs.Backend.id;
const AppletCapabilityIDs = Specs.Applet.CapabilityIDs;

const adjustUrlBase = (urlString: string, newUrl: string): string => {
  const newUrlUrl = new URL(newUrl);

  const url = new URL(urlString);
  if (url.hostname === 'localhost') {
    url.protocol = newUrlUrl.protocol;
    url.hostname = newUrlUrl.hostname;
    url.port = newUrlUrl.port;
    url.pathname = `${newUrlUrl.pathname.replace(/\/$/u, '')}${url.pathname}`;
  }
  return url.toString();
};

const appendIndexHtml = (urlString: string): string => {
  const url = new URL(urlString);
  url.pathname += `/index.html`;
  return url.toString();
};

const isLocalhostUrl = (urlString: string): boolean => {
  const url = new URL(urlString);
  return url.hostname === 'localhost';
};

const mapAppletUrl = (value: unknown, appUrl: string): string => {
  if (typeof value !== 'string') {
    return '';
  }
  let url = value;
  url = adjustUrlBase(url, appUrl);
  // Don't append index.html for localhost URLs (local development)
  if (isLocalhostUrl(appUrl)) {
    return url;
  }
  return appendIndexHtml(url);
};

const mapBackendUrl = (value: unknown, backendUrl: string): string => {
  if (typeof value !== 'string') {
    return '';
  }
  const url = value;
  return adjustUrlBase(url, backendUrl);
};

interface CapabilityConfig {
  type: string;
  url?: string;
  [key: string]: unknown;
}

/**
 * Rebases localhost URLs in a manifest to point to the provided base URL.
 * This is used for sideloading extensions where the manifest contains localhost
 * URLs that need to be replaced with the actual deployment URL.
 *
 * @param manifest - The extension manifest to rebase
 * @param appUrl - The base URL for applet capabilities (e.g., https://my-app.doxyme-ephemeral.app/branch-123/)
 * @param backendUrl - Optional base URL for backend capability
 * @returns The manifest with rebased URLs
 */
export const rebaseManifestUrls = (
  manifest: Manifest,
  appUrl?: string,
  backendUrl?: string,
): Manifest => {
  const capabilities = manifest.capabilities as CapabilityConfig[];

  return {
    ...manifest,
    capabilities: capabilities.map((capability: CapabilityConfig) => {
      if (capability.type === BackendCapabilityID && backendUrl !== undefined) {
        return {
          ...capability,
          url: mapBackendUrl(capability.url, backendUrl),
        };
      } else if (
        AppletCapabilityIDs.includes(capability.type) &&
        appUrl !== undefined
      ) {
        return { ...capability, url: mapAppletUrl(capability.url, appUrl) };
      }
      return capability;
    }),
  };
};
